import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CloudArrowDown, CloudArrowUp, Warning, Database, FileXls } from "@phosphor-icons/react";

export default function DataBackup() {
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [exportingTally, setExportingTally] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);

  const downloadBlob = (res, fallbackName) => {
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : fallbackName;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportBackup = async () => {
    setExporting(true);
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      downloadBlob(res, `vetrich-backup-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Backup downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(false); }
  };

  const exportTally = async () => {
    setExportingTally(true);
    try {
      const res = await api.get("/backup/export-tally", { responseType: "blob" });
      downloadBlob(res, `vetrich-tally-${new Date().toISOString().slice(0, 10)}.xml`);
      toast.success("Tally XML downloaded — import via Gateway of Tally → Import Data");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExportingTally(false); }
  };

  const importBackup = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!window.confirm(`Restore data from "${f.name}"?\n\nRecords with the same IDs will be overwritten in place. Take a fresh export first if you're unsure.`)) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastImport(data);
      toast.success(`Restored ${data.total} document(s) across ${Object.keys(data.restored || {}).length} collection(s)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); }
  };

  return (
    <div className="page-shell space-y-6">
      <div>
        <div className="eyebrow flex items-center gap-1.5"><Database size={11} /> Data safety</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Data Backup</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Download a full JSON snapshot of the active company's data, or restore from a previous export.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-flat p-6 space-y-3">
          <div className="eyebrow">Export · Full</div>
          <h2 className="font-heading text-xl font-semibold">Download full backup</h2>
          <p className="text-sm text-muted-foreground">
            One JSON file with every customer, supplier, product, invoice, voucher, cheque, DSC record,
            tender, credential and setting for the active company.
          </p>
          <Button size="lg" onClick={exportBackup} disabled={exporting} data-testid="backup-export-btn">
            <CloudArrowDown size={16} className="mr-1.5" />
            {exporting ? "Preparing…" : "Export All"}
          </Button>
        </div>

        <div className="card-flat p-6 space-y-3">
          <div className="eyebrow">Export · Tally</div>
          <h2 className="font-heading text-xl font-semibold">Download Tally XML</h2>
          <p className="text-sm text-muted-foreground">
            One XML file ready to import into Tally.ERP 9 / TallyPrime. Includes customer &amp;
            supplier ledgers, stock items, and Sales / Purchase / Receipt / Payment vouchers.
          </p>
          <div className="rounded-md border border-primary/30 bg-primary/[.05] px-3 py-2 text-[11.5px] text-primary/90">
            In Tally: <b>Gateway of Tally → Import Data → Masters / Vouchers</b> — paste the downloaded XML file path.
          </div>
          <Button size="lg" variant="outline" onClick={exportTally} disabled={exportingTally} data-testid="backup-export-tally-btn">
            <FileXls size={16} className="mr-1.5" />
            {exportingTally ? "Preparing…" : "Export for Tally"}
          </Button>
        </div>

        <div className="card-flat p-6 space-y-3">
          <div className="eyebrow">Import</div>
          <h2 className="font-heading text-xl font-semibold">Restore from backup</h2>
          <p className="text-sm text-muted-foreground">
            Upload a previously exported JSON file. Records are matched by their unique ID and
            overwritten in place — safe to re-import the same file.
          </p>
          <div className="rounded-md border border-amber-400/60 bg-amber-50 dark:bg-amber-500/5 px-3 py-2 text-[11.5px] text-amber-900 dark:text-amber-200 flex items-start gap-2">
            <Warning size={14} className="mt-0.5" />
            <span>Destructive operation — existing records with the same IDs will be replaced. Take a fresh export first.</span>
          </div>
          <input
            type="file"
            ref={fileRef}
            accept="application/json,.json"
            className="hidden"
            onChange={importBackup}
            data-testid="backup-import-input"
          />
          <Button
            size="lg"
            variant="outline"
            disabled={importing}
            onClick={() => fileRef.current?.click()}
            data-testid="backup-import-btn"
          >
            <CloudArrowUp size={16} className="mr-1.5" />
            {importing ? "Restoring…" : "Import Backup"}
          </Button>
        </div>
      </div>

      {lastImport && (
        <div className="card-flat p-5">
          <div className="eyebrow">Last import</div>
          <div className="font-heading text-lg font-semibold mb-3">
            {lastImport.total} document(s) restored
          </div>
          <table className="data-table" data-testid="backup-last-import-table">
            <thead><tr><th>Collection</th><th className="num">Records restored</th></tr></thead>
            <tbody>
              {Object.entries(lastImport.restored || {})
                .filter(([, n]) => n > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([c, n]) => (
                  <tr key={c}>
                    <td className="font-mono">{c}</td>
                    <td className="num font-mono">{n}</td>
                  </tr>
                ))}
              {Object.keys(lastImport.restored || {}).length === 0 && (
                <tr><td colSpan={2} className="text-center text-muted-foreground py-4">Nothing to restore in that file.</td></tr>
              )}
            </tbody>
          </table>
          {lastImport.meta?.exported_at && (
            <div className="text-[11px] text-muted-foreground mt-3">
              Source snapshot exported at {String(lastImport.meta.exported_at).slice(0, 19).replace("T", " ")} by {lastImport.meta.exported_by || "—"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
